import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { apiJson } from '../lib/api'

// Better Auth handles the OAuth callback inside /api/auth/callback/google
// and sets the session cookie before this page mounts. All we do here is
// fetch the canonical profile and route based on whether the user has
// picked a role yet.
//
// `?intended_role=` is the M5 Phase 5 signal from the landing-page CTA
// fork ("I'm an agent" / "I'm a coach"). If the user is brand new (role
// still null), we forward it to /onboarding for pre-selection. Returning
// users with an existing role go straight to /dashboard and the hint is
// silently dropped — role is single-shot per spec §7.

interface MeResponse {
  profile: { role: string | null }
}

const VALID_INTENT = new Set(['agent', 'coach', 'player'])

export default function AuthCallback() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [message, setMessage] = useState('Signing you in…')

  useEffect(() => {
    let cancelled = false
    async function complete() {
      try {
        const { profile } = await apiJson<MeResponse>('/api/me')
        if (cancelled) return
        if (profile.role) {
          navigate('/dashboard', { replace: true })
          return
        }
        const intent = searchParams.get('intended_role') ?? ''
        const onboardingPath = VALID_INTENT.has(intent)
          ? `/onboarding?intended_role=${encodeURIComponent(intent)}`
          : '/onboarding'
        navigate(onboardingPath, { replace: true })
      } catch {
        if (cancelled) return
        setMessage('Something went wrong. Redirecting…')
        setTimeout(() => navigate('/', { replace: true }), 1000)
      }
    }
    complete()
    return () => {
      cancelled = true
    }
  }, [navigate, searchParams])

  return (
    <main className="min-h-screen flex items-center justify-center bg-hoop-bg">
      <p className="text-hoop-text-3">{message}</p>
    </main>
  )
}
